'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { RankingEntry } from '../../lib/admin';

interface RankingCardProps {
  entry: RankingEntry;
  isCurrentUser?: boolean;
  className?: string;
}

export function RankingCard({ entry, isCurrentUser = false, className }: RankingCardProps) {
  const getMedalIcon = (ranking_position: number) => {
    if (ranking_position === 1) return 'ri-medal-fill text-yellow-500';
    if (ranking_position === 2) return 'ri-medal-fill text-gray-400';
    if (ranking_position === 3) return 'ri-medal-fill text-amber-600';
    return null;
  };

  const getMedalBg = (ranking_position: number) => {
    if (ranking_position === 1) return 'bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 border-yellow-400/30';
    if (ranking_position === 2) return 'bg-gradient-to-br from-gray-300/20 to-gray-500/20 border-gray-400/30';
    if (ranking_position === 3) return 'bg-gradient-to-br from-amber-400/20 to-amber-600/20 border-amber-500/30';
    return '';
  };

  const abbreviateEmail = (email: string): string => {
    if (!email) return 'Usuário';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 20) {
      return email;
    }
    return `${localPart.substring(0, 20)}...`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
        className={cn(
          'relative rounded-xl border border-gray-200 dark:border-[#8a2be2]/30 p-4 transition-all duration-200',
          isCurrentUser 
            ? 'bg-gradient-to-r from-[#6a0dad]/10 to-[#8a2be2]/10 dark:from-[#6a0dad]/20 dark:to-[#8a2be2]/20 border-[#6a0dad] dark:border-[#8a2be2] ring-2 ring-[#6a0dad]/20 dark:ring-[#8a2be2]/20'
            : 'bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] hover:shadow-lg',
        entry.ranking_position <= 3 && getMedalBg(entry.ranking_position),
        className
      )}
    >
      {/* Badge de posição */}
      <div className="flex items-center space-x-4">
        <div className={cn(
          'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg',
          entry.ranking_position <= 3 
            ? 'bg-gradient-to-br from-[#6a0dad] to-[#8a2be2] text-white'
            : 'bg-gray-100 dark:bg-[#0d0d0d] text-gray-700 dark:text-[#c780ff]'
        )}>
          {entry.ranking_position <= 3 && getMedalIcon(entry.ranking_position) ? (
            <i className={cn('text-2xl', getMedalIcon(entry.ranking_position))}></i>
          ) : (
            <span className="font-ubuntu">{entry.ranking_position}</span>
          )}
        </div>

        {/* Avatar e Nome */}
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#6a0dad] to-[#8a2be2] flex items-center justify-center text-white font-semibold flex-shrink-0">
            <i className="ri-user-line text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn(
              'text-sm font-semibold truncate font-ubuntu',
              isCurrentUser 
                ? 'text-[#6a0dad] dark:text-[#e0e1dd]' 
                : 'text-gray-900 dark:text-[#e0e1dd]'
            )}>
              {abbreviateEmail(entry.email)}
              {isCurrentUser && (
                <span className="ml-2 text-xs text-[#6a0dad] dark:text-[#c780ff]">(Você)</span>
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">
              Nível {entry.level} • {entry.points.toLocaleString('pt-BR')} pontos
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="hidden md:flex items-center space-x-6 flex-shrink-0">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
              {entry.cards_created_count.toLocaleString('pt-BR')}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">Cards</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
              {entry.codes_copied_count.toLocaleString('pt-BR')}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">Copiados</p>
          </div>
        </div>
      </div>

      {/* Stats Mobile */}
      <div className="md:hidden mt-4 pt-4 border-t border-gray-200 dark:border-[#778da9]/30 grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
            {entry.cards_created_count.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-gray-500 dark:text-[#778da9] font-montserrat">Cards Criados</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
            {entry.codes_copied_count.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-gray-500 dark:text-[#778da9] font-montserrat">Códigos Copiados</p>
        </div>
      </div>
    </motion.div>
  );
}
